import { useEffect, useRef, useState } from "react";
import { Copy, Power, RefreshCw } from "lucide-react";
import { api } from "./api";

const permissions = { screen: true, mouse: true, keyboard: true, files: true };

function iceServers() {
  const result = [{ urls: "stun:stun.l.google.com:19302" }];
  const urls = String(import.meta.env.VITE_TURN_URLS || "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  if (urls.length) {
    result.push({
      urls,
      username: import.meta.env.VITE_TURN_USERNAME || "",
      credential: import.meta.env.VITE_TURN_CREDENTIAL || "",
    });
  }
  return result;
}

export default function Remote({
  user,
  socket,
  sessionMessage,
  activeSession,
  acceptedScreenStream,
  onDisconnectSession,
  isVisible = true,
}) {
  const [code, setCode] = useState("------");
  const [expires, setExpires] = useState(null);
  const [target, setTarget] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("Ready");
  const [sharing, setSharing] = useState(false);
  const [connectionState, setConnectionState] = useState("idle");
  const [viewerControlEnabled, setViewerControlEnabled] = useState(false);
  const [mouseControlEnabled, setMouseControlEnabled] = useState(true);
  const [keyboardControlEnabled, setKeyboardControlEnabled] = useState(true);
  const localVideo = useRef(null);
  const remoteVideo = useRef(null);
  const peer = useRef(null);
  const stream = useRef(null);
  const iceQueue = useRef([]);
  const makingOffer = useRef(false);
  const viewerReady = useRef(false);
  const controlChannel = useRef(null);
  const pendingMouseMove = useRef(null);
  const mouseFrame = useRef(0);

  const sessionId = activeSession?.sessionId;
  const isHost = Boolean(activeSession && String(activeSession.host) === String(user.id));
  const isViewer = Boolean(activeSession && String(activeSession.guest) === String(user.id));

  async function rotate() {
    try {
      const data = await api("/api/auth/rotate-access-password", { method: "POST" });
      setCode(data.accessPassword);
      setExpires(new Date(data.expiresAt));
    } catch (error) {
      setStatus(error.message);
    }
  }

  useEffect(() => {
    rotate();
    const timer = setInterval(rotate, 120000);
    return () => clearInterval(timer);
  }, []);

  async function connect() {
    try {
      if (!target.trim() || !password.trim()) {
        return setStatus("Enter the target Orbit ID and temporary password.");
      }
      const data = await api("/api/auth/verify-access", {
        method: "POST",
        body: JSON.stringify({
          orbitId: target.replace(/\D/g, ""),
          accessPassword: password.trim(),
        }),
      });
      socket.timeout(10000).emit(
        "session:request",
        {
          targetId: String(data.target.id),
          accessGrant: data.accessGrant,
          permissions,
          deviceId: localStorage.getItem("deviceId"),
        },
        (timeoutError, response) => {
          if (timeoutError) return setStatus("Server did not respond.");
          setStatus(response?.ok ? "Request delivered. Waiting for approval…" : response?.message || "Request failed.");
        },
      );
    } catch (error) {
      setStatus(error.message || "Unable to connect");
    }
  }

  function destroyPeer(stopCapture = true) {
    if (mouseFrame.current) cancelAnimationFrame(mouseFrame.current);
    mouseFrame.current = 0;
    pendingMouseMove.current = null;
    if (controlChannel.current) {
      controlChannel.current.onopen = null;
      controlChannel.current.onclose = null;
      controlChannel.current.onmessage = null;
      controlChannel.current.close();
      controlChannel.current = null;
    }
    window.orbitDesktop?.setRemoteControlEnabled?.(false);
    if (peer.current) {
      peer.current.onicecandidate = null;
      peer.current.ontrack = null;
      peer.current.onconnectionstatechange = null;
      peer.current.close();
      peer.current = null;
    }
    iceQueue.current = [];
    setConnectionState("idle");
    if (stopCapture && stream.current) {
      stream.current.getTracks().forEach((track) => track.stop());
      stream.current = null;
      setSharing(false);
    }
    if (stopCapture && localVideo.current) localVideo.current.srcObject = null;
    if (remoteVideo.current) remoteVideo.current.srcObject = null;
  }

  function handleHostControlMessage(event) {
    if (!isHost || !window.orbitDesktop?.isDesktop) return;
    try {
      const input = JSON.parse(event.data);
      if ((input.type === "wheel" || input.type?.startsWith("mouse")) && !activeSession?.permissions?.mouse) return;
      if (input.type?.startsWith("key") && !activeSession?.permissions?.keyboard) return;
      window.orbitDesktop.remoteInput(input);
    } catch (error) {
      console.warn("Invalid remote control message:", error);
    }
  }

  function configureControlChannel(channel) {
    controlChannel.current = channel;
    channel.onopen = () => setStatus("Screen connected. Remote control ready.");
    channel.onclose = () => setStatus("Remote control channel closed.");
    if (isHost) channel.onmessage = handleHostControlMessage;
  }

  function sendControl(input) {
    if (!isViewer || !activeSession || !viewerControlEnabled) return;
    const channel = controlChannel.current;
    if (!channel || channel.readyState !== "open") return;
    if (input.type === "mouse-move" && channel.bufferedAmount > 65536) return;
    channel.send(JSON.stringify(input));
  }

  function videoPoint(event) {
    const rect = remoteVideo.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return null;
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  }

  function handleMouseMove(event) {
    if (!viewerControlEnabled || !mouseControlEnabled || !activeSession?.permissions?.mouse) return;
    const point = videoPoint(event);
    if (!point) return;
    pendingMouseMove.current = { type: "mouse-move", ...point };
    if (mouseFrame.current) return;
    mouseFrame.current = requestAnimationFrame(() => {
      mouseFrame.current = 0;
      if (pendingMouseMove.current) sendControl(pendingMouseMove.current);
      pendingMouseMove.current = null;
    });
  }

  function handleMouseButton(event, type) {
    if (!viewerControlEnabled || !mouseControlEnabled || !activeSession?.permissions?.mouse) return;
    event.preventDefault();
    remoteVideo.current?.focus();
    const point = videoPoint(event);
    if (point) sendControl({ type, button: event.button, ...point });
  }

  function handleWheel(event) {
    if (!viewerControlEnabled || !mouseControlEnabled || !activeSession?.permissions?.mouse) return;
    event.preventDefault();
    sendControl({ type: "wheel", deltaX: event.deltaX, deltaY: event.deltaY });
  }

  function handleKey(event, type) {
    if (!viewerControlEnabled || !keyboardControlEnabled || !activeSession?.permissions?.keyboard) return;
    event.preventDefault();
    sendControl({ type, key: event.key, code: event.code, repeat: event.repeat });
  }

  async function optimiseVideoSender(pc) {
    const sender = pc
      .getSenders()
      .find((item) => item.track?.kind === "video");

    if (!sender) return;

    try {
      const parameters = sender.getParameters();
      if (!parameters.encodings?.length) parameters.encodings = [{}];
      parameters.degradationPreference = "maintain-framerate";
      parameters.encodings[0] = {
        ...parameters.encodings[0],
        maxBitrate: 1_800_000,
        maxFramerate: 24,
        scaleResolutionDownBy: 1,
      };
      await sender.setParameters(parameters);
    } catch (error) {
      console.warn("Unable to tune screen sender:", error);
    }
  }

  function createPeer() {
    if (peer.current && peer.current.signalingState !== "closed") return peer.current;
    const pc = new RTCPeerConnection({
      iceServers: iceServers(),
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
      iceCandidatePoolSize: 4,
    });
    peer.current = pc;
    pc.ondatachannel = ({ channel }) => {
      if (channel.label === "orbit-control") configureControlChannel(channel);
    };
    if (isHost) {
      configureControlChannel(
        pc.createDataChannel("orbit-control", { ordered: true }),
      );
    }
    pc.onicecandidate = ({ candidate }) => {
      if (candidate && sessionId) socket.emit("webrtc:ice", { sessionId, candidate });
    };
    pc.ontrack = ({ streams, track }) => {
      const remoteStream = streams?.[0] || new MediaStream([track]);
      if (remoteVideo.current) {
        remoteVideo.current.srcObject = remoteStream;
        remoteVideo.current.play().catch(() => {});
      }
      setStatus("Partner screen connected.");
    };
    pc.onconnectionstatechange = () => {
      setConnectionState(pc.connectionState);
      if (pc.connectionState === "connected") setStatus("Secure screen connection established.");
      if (pc.connectionState === "failed") setStatus("Connection failed. Configure TURN for different networks.");
      if (pc.connectionState === "disconnected") setStatus("Partner connection interrupted…");
    };
    if (stream.current) {
      stream.current.getTracks().forEach((track) => pc.addTrack(track, stream.current));
      optimiseVideoSender(pc);
    }
    return pc;
  }

  async function sendOffer() {
    if (!isHost || !stream.current || !sessionId || makingOffer.current) return;
    const pc = createPeer();
    if (pc.signalingState !== "stable") return;
    try {
      makingOffer.current = true;
      await optimiseVideoSender(pc);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit("webrtc:offer", { sessionId, description: pc.localDescription });
      setStatus("Screen offer sent. Waiting for partner…");
    } catch (error) {
      setStatus(`Unable to send screen: ${error.message}`);
    } finally {
      makingOffer.current = false;
    }
  }

  async function startAcceptedStream(selected) {
    if (!isHost || !selected) return;
    try {
      if (stream.current) stream.current.getTracks().forEach((track) => track.stop());
      stream.current = selected;
      const videoTrack = selected.getVideoTracks()[0];
      if (!videoTrack) throw new Error("Screen video track was not created");
      videoTrack.contentHint = "motion";
      videoTrack.enabled = isVisible;
      await videoTrack
        .applyConstraints({
          width: { ideal: 1280, max: 1600 },
          height: { ideal: 720, max: 900 },
          frameRate: { ideal: 20, max: 24 },
        })
        .catch((error) => {
          console.warn("Unable to apply screen constraints:", error);
        });
      setSharing(true);
      if (localVideo.current) {
        localVideo.current.srcObject = selected;
        localVideo.current.play().catch(() => {});
      }
      destroyPeer(false);
      createPeer();
      videoTrack.addEventListener("ended", () => {
        destroyPeer(true);
        setStatus("Screen sharing stopped.");
        onDisconnectSession?.();
      });
      if (viewerReady.current) await sendOffer();
      else setStatus("Screen selected. Waiting for partner…");
    } catch (error) {
      setStatus(`Unable to start screen sharing: ${error.message}`);
    }
  }

  useEffect(() => {
    if (isHost && acceptedScreenStream && acceptedScreenStream !== stream.current) {
      startAcceptedStream(acceptedScreenStream);
    }
  }, [isHost, acceptedScreenStream, sessionId]);

  useEffect(() => {
    // Navigation बदलने पर peer को बंद नहीं करते। केवल outgoing video track
    // pause/resume होता है, इसलिए Remote tab पर लौटते ही live screen चलती है।
    if (isHost) {
      const videoTrack = stream.current?.getVideoTracks?.()[0];
      if (videoTrack && videoTrack.readyState === "live") {
        videoTrack.enabled = isVisible;
      }
    }

    if (isVisible) {
      localVideo.current?.play().catch(() => {});
      remoteVideo.current?.play().catch(() => {});
      if (activeSession) setStatus("Remote screen resumed.");
    } else if (activeSession && isHost) {
      setStatus("Screen paused while another section is open.");
    }
  }, [isVisible, isHost, activeSession?.sessionId]);

  useEffect(() => {
    const enabled = Boolean(
      isHost && activeSession && window.orbitDesktop?.isDesktop &&
      (activeSession.permissions?.mouse || activeSession.permissions?.keyboard),
    );
    window.orbitDesktop?.setRemoteControlEnabled?.(enabled);
    const stopListening = window.orbitDesktop?.onEmergencyStop?.(() => {
      setStatus("Remote control stopped on host with Ctrl+Alt+Escape.");
    });
    return () => {
      stopListening?.();
      window.orbitDesktop?.setRemoteControlEnabled?.(false);
    };
  }, [isHost, activeSession?.sessionId]);

  useEffect(() => {
    if (!socket || !sessionId) return;
    const matches = (payload) => payload?.sessionId === sessionId;
    const flushIce = async (pc) => {
      const candidates = iceQueue.current.splice(0);
      for (const candidate of candidates) await pc.addIceCandidate(candidate).catch(console.error);
    };
    const ready = (payload) => {
      if (matches(payload) && isHost) {
        viewerReady.current = true;
        if (stream.current) sendOffer();
        else setStatus("Partner ready. Waiting for screen permission…");
      }
    };
    const offer = async (payload) => {
      if (!matches(payload) || !isViewer || !payload.description) return;
      try {
        const pc = createPeer();
        await pc.setRemoteDescription(payload.description);
        await flushIce(pc);
        await pc.setLocalDescription(await pc.createAnswer());
        socket.emit("webrtc:answer", { sessionId, description: pc.localDescription });
        setStatus("Receiving partner screen…");
      } catch (error) {
        setStatus(`Unable to receive screen: ${error.message}`);
      }
    };
    const answer = async (payload) => {
      if (!matches(payload) || !isHost || !payload.description) return;
      const pc = createPeer();
      try {
        await pc.setRemoteDescription(payload.description);
        await flushIce(pc);
      } catch (error) {
        setStatus(`Partner answer failed: ${error.message}`);
      }
    };
    const ice = async (payload) => {
      if (!matches(payload) || !payload.candidate) return;
      const pc = createPeer();
      if (!pc.remoteDescription) iceQueue.current.push(payload.candidate);
      else await pc.addIceCandidate(payload.candidate).catch(console.error);
    };
    const peerJoined = (payload) => {
      if (matches(payload) && isViewer) socket.emit("webrtc:ready", { sessionId });
    };

    socket.on("webrtc:ready", ready);
    socket.on("webrtc:offer", offer);
    socket.on("webrtc:answer", answer);
    socket.on("webrtc:ice", ice);
    socket.on("session:peer-joined", peerJoined);
    socket.timeout(10000).emit("session:join", { sessionId }, (error, response) => {
      if (error || !response?.ok) return setStatus(response?.message || "Unable to join session.");
      if (isViewer) {
        socket.emit("webrtc:ready", { sessionId });
        setStatus("Waiting for partner to share their screen…");
      } else setStatus("Session joined. Starting screen sharing…");
    });

    return () => {
      socket.off("webrtc:ready", ready);
      socket.off("webrtc:offer", offer);
      socket.off("webrtc:answer", answer);
      socket.off("webrtc:ice", ice);
      socket.off("session:peer-joined", peerJoined);
      destroyPeer(true);
      viewerReady.current = false;
    };
  }, [socket, sessionId, isHost, isViewer]);

  async function copyOrbitId() {
    try {
      await navigator.clipboard.writeText(
        String(user.orbitId || "").replace(/\D/g, ""),
      );
      setStatus("Orbit ID copied.");
    } catch (error) {
      setStatus("Clipboard access was denied. Copy the Orbit ID manually.");
    }
  }

  function disconnect() {
    destroyPeer(true);
    viewerReady.current = false;
    onDisconnectSession?.();
    setStatus("Remote session disconnected.");
  }

  useEffect(() => {
    setViewerControlEnabled(false);
  }, [sessionId]);

  function toggleViewerControl() {
    if (!isViewer) return;
    if (!mouseControlEnabled && !keyboardControlEnabled) {
      setStatus("Select Mouse Control or Keyboard Control first.");
      return;
    }
    const nextValue = !viewerControlEnabled;
    setViewerControlEnabled(nextValue);
    if (nextValue) {
      remoteVideo.current?.focus();
      setStatus("Remote control enabled. Use the partner screen below.");
    } else {
      setStatus("Remote control paused on this viewer.");
    }
  }

  return (
    <>
      <div className="hero">
        <div>
          <small>THIS DEVICE</small>
          <h2>{String(user.orbitId || "").replace(/\D/g, "")}</h2>
          <button className="icon" onClick={copyOrbitId} title="Copy Orbit ID">
            <Copy size={18} />
          </button>
          <p>Share this ID and temporary password only with people you trust.</p>
        </div>
        <div className="pass">
          <small>2-MINUTE PASSWORD</small>
          <strong>{code}</strong>
          <button className="icon" onClick={rotate}><RefreshCw size={18} /></button>
          <span>{expires && `Expires ${expires.toLocaleTimeString()}`}</span>
        </div>
      </div>
      <div className="grid2">
        <section className="panel">
          <h3>Connect to another device</h3>
          <input value={target} onChange={(event) => setTarget(event.target.value.replace(/\D/g, "").slice(0, 9))} placeholder="Orbit ID: 123456789" />
          <input value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Temporary password" />
          <div className="checks">Screen • Mouse • Keyboard • Files</div>
          <button onClick={connect}>Request secure session</button>
          <p className="muted">{activeSession ? status : sessionMessage || status}</p>
        </section>
        <section className="panel">
          <h3>{isViewer ? "Partner screen" : "Share this screen"}</h3>
          <video
            ref={isViewer ? remoteVideo : localVideo}
            autoPlay
            playsInline
            muted
            controls={false}
            tabIndex={isViewer ? 0 : -1}
            onMouseMove={isViewer ? handleMouseMove : undefined}
            onMouseDown={isViewer ? (event) => handleMouseButton(event, "mouse-down") : undefined}
            onMouseUp={isViewer ? (event) => handleMouseButton(event, "mouse-up") : undefined}
            onWheel={isViewer ? handleWheel : undefined}
            onKeyDown={isViewer ? (event) => handleKey(event, "key-down") : undefined}
            onKeyUp={isViewer ? (event) => handleKey(event, "key-up") : undefined}
            onContextMenu={isViewer ? (event) => event.preventDefault() : undefined}
            style={{
              cursor: isViewer && viewerControlEnabled ? "crosshair" : "default",
              outline: viewerControlEnabled ? "3px solid #0b5ed7" : "none",
            }}
          />
          {isHost && <p className="muted">{sharing ? "Your screen is being shared automatically." : "Preparing automatic screen sharing…"}</p>}
          {isViewer && activeSession && (
            <div
              style={{
                padding: 14,
                marginBottom: 12,
                border: "1px solid #dbe5f3",
                borderRadius: 12,
                background: "#f7faff",
              }}
            >
              <strong style={{ display: "block", marginBottom: 10 }}>
                Viewer controls
              </strong>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <input
                    type="checkbox"
                    checked={mouseControlEnabled}
                    disabled={!activeSession.permissions?.mouse}
                    onChange={(event) => setMouseControlEnabled(event.target.checked)}
                    style={{ width: "auto", margin: 0 }}
                  />
                  Mouse control
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <input
                    type="checkbox"
                    checked={keyboardControlEnabled}
                    disabled={!activeSession.permissions?.keyboard}
                    onChange={(event) => setKeyboardControlEnabled(event.target.checked)}
                    style={{ width: "auto", margin: 0 }}
                  />
                  Keyboard control
                </label>
              </div>
              <button
                type="button"
                onClick={toggleViewerControl}
                style={{
                  width: "100%",
                  background: viewerControlEnabled ? "#d97706" : "#0b5ed7",
                }}
              >
                {viewerControlEnabled ? "Stop Control" : "Start Control"}
              </button>
              <small style={{ display: "block", marginTop: 9, color: "#64748b" }}>
                {viewerControlEnabled
                  ? "Control is active. Click the video before typing."
                  : "Control is off until you press Start Control."}
              </small>
            </div>
          )}
          {!activeSession && <p className="muted">No active remote session.</p>}
          {activeSession && (
            <>
              <p className="muted">Session {sessionId.slice(0, 8)} • {connectionState}</p>
              <button
                type="button"
                onClick={disconnect}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  width: "100%",
                  marginTop: 10,
                  background: "#dc2626",
                }}
              >
                <Power size={18} />
                Disconnect session
              </button>
            </>
          )}
        </section>
      </div>
    </>
  );
}
